import {
  Avatar,
  Box,
  Button,
  DialogContent,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  IDisabilityConfirmation,
  IEligibleFamilyMember,
  getEligibleFamilyMembers,
  getFamilyMembersConfirmationPdf,
} from 'API/disabilityConfirmation';
import { Loader } from 'Components';
import { CustomDialog } from 'Components/CustomDialog';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Download2, LeftArrow, PurpleAlert } from 'Images';
import { downloadFile } from 'Utils';
import { useEffect, useState } from 'react';

interface IFamilyMembersDialog {
  confirmation: IDisabilityConfirmation;
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export const FamilyMembersDialog = ({
  confirmation,
  showDialog,
  setShowDialog,
}: IFamilyMembersDialog) => {
  const [familyMembers, setFamilyMembers] = useState<IEligibleFamilyMember[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);

  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.DisabilityApprovals);

  const initFamilyMembers = async () => {
    const fmResult = await getEligibleFamilyMembers();
    if (fmResult) {
      setFamilyMembers(fmResult);
    }
  };

  useEffect(() => {
    initFamilyMembers();
  }, []);

  const onClick = async (id: number, name: string) => {
    setIsLoading(true);
    const file = await getFamilyMembersConfirmationPdf(id);
    if (file && file.file) {
      downloadFile(file.file, 'pdf', name);
    }
    setIsLoading(false);
  };

  const getAriaDescriptionIds = () => {
    let descriptionIds = '';
    (familyMembers.length > 0
      ? confirmation.description.split(',')
      : dictionary.noFamilyMembers
    ).forEach((__, index: number) => {
      descriptionIds += `confirmation-description-${index} `;
      return;
    });
    return descriptionIds;
  };

  return (
    <CustomDialog
      height='auto !important'
      showDialog={showDialog}
      onclose={() => setShowDialog(false)}
      top='55%'
    >
      <DialogContent
        sx={{
          padding: isDesktop ? '24px 64px 16px' : '10px 20px',
          marginTop: '24px',
          zIndex: '1',
          maxHeight: isDesktop ? undefined : '70vh',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
        }}
      >
        <Stack
          m='72px 0 48px'
          gap='40px'
          alignItems='center'
          justifyContent='center'
        >
          <Stack gap='12px' alignItems='center' justifyContent='center'>
            <Typography id='confirmation-title' variant='h5'>
              {confirmation.additionalTitle}
            </Typography>
            <Stack>
              {(familyMembers.length > 0
                ? (confirmation.additionalDescription || '').split(',')
                : dictionary.noFamilyMembers
              ).map((text: string, index: number, array: any[]) => (
                <Typography
                  id={`confirmation-description-${index}`}
                  textAlign='center'
                  variant='body5'
                >
                  {`${text}${index === array.length - 1 ? '' : ', '}`}
                </Typography>
              ))}
            </Stack>
          </Stack>
          {familyMembers.length > 1 && (
            <Typography variant='body6' mb={isDesktop ? undefined : '-26px'}>
              {dictionary.forWhom}
            </Typography>
          )}
          {familyMembers.length > 0 && (
            <Stack
              alignItems='center'
              justifyContent={
                isDesktop && familyMembers.length > 4 ? 'flex-start' : 'center'
              }
              p={isDesktop && familyMembers.length > 4 ? '0 15px' : undefined}
              gap='16px'
              component='fieldset'
              flexDirection={isDesktop ? 'row' : 'column'}
              flexWrap='wrap'
              aria-labelledby='confirmation-title'
              aria-describedby={getAriaDescriptionIds()}
            >
              <legend>{dictionary.forWhom}</legend>
              {isLoading ? (
                <Box
                  position={'relative'}
                  sx={{
                    top: '0',
                    height: '70px ',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    m: '0 auto',
                  }}
                >
                  <Loader size={50} />
                </Box>
              ) : (
                familyMembers.map((familyMember) => (
                  <Button
                    variant='secondary1'
                    sx={{
                      width: isDesktop ? '141px' : '208px',
                      gap: '4px',
                    }}
                    key={familyMember.identityNumber}
                    onClick={() =>
                      onClick(
                        familyMember.id,
                        `${familyMember.firstName} ${familyMember.lastName}`
                      )
                    }
                  >
                    <Avatar
                      alt=''
                      src={Download2}
                      sx={{ width: '16px', height: '16px' }}
                    />
                    <Typography variant='body4'>
                      {familyMember.firstName}
                    </Typography>
                  </Button>
                ))
              )}
            </Stack>
          )}
          <Stack
            direction='row'
            alignItems='flex-start'
            justifyContent='center'
            gap='8px'
          >
            <Avatar
              alt=''
              src={PurpleAlert}
              sx={{ width: '24px', height: '24px' }}
            />
            <Stack
              sx={
                isDesktop
                  ? {
                      flexDirection: 'row',
                      gap: '8px',
                      width: 'fit-content',
                    }
                  : undefined
              }
            >
              <Typography variant='body3'>
                {familyMembers.length > 0
                  ? dictionary.isSomeoneMissing
                  : dictionary.weAreNotUpdated}
              </Typography>
              <Link
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
                underline='none'
                href={dictionary.updateUsUrl}
              >
                <Typography variant='body3'>
                  {familyMembers.length > 0
                    ? dictionary.updateUs
                    : dictionary.canUpdate}
                </Typography>
                <Avatar
                  alt=''
                  src={LeftArrow}
                  sx={{ width: '20px', height: '20px', mt: '3px' }}
                />
              </Link>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </CustomDialog>
  );
};
